import { useState } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MuiLink from '@mui/material/Link';
import { makeStyles } from 'tss-react/mui';

import { WalletModal } from 'modules/auth/components/WalletModal';
import {
  DOCUMENTATION_LINK,
  FEEDBACK_LINK,
  useHeaderHeight,
} from 'modules/common';
import { useTranslation } from 'modules/i18n';
import { getAccountAddress } from 'modules/store';

import logoIcon from '../assets/ankr-logo.svg';
import { Account } from './Account';
import translation from './header.translation';
import { TestLabel } from './TestLabel/TestLabel';

interface Props {
  className?: string;
}

export function Header({ className }: Props): JSX.Element {
  const { classes, cx } = useStyles();
  const [showModal, setShowModal] = useState(false);
  const { t, keys } = useTranslation(translation);
  const height = useHeaderHeight();

  const { data: accountAddress } = getAccountAddress.useQueryState();

  return (
    <Box
      component="header"
      className={cx(classes.container, className)}
      height={height}
    >
      <div className={classes.info}>
        <Link className={classes.logo} to="/">
          <img src={logoIcon} alt="Ankr Automate" />
        </Link>

        <TestLabel />
      </div>

      <div className={classes.panel}>
        <div className={classes.links}>
          <MuiLink
            data-tid="layout-header-documentation-link"
            variant="body2"
            href={DOCUMENTATION_LINK}
            target="_blank"
            rel="noreferrer"
          >
            {t(keys.documentation)}
          </MuiLink>

          <MuiLink
            data-tid="layout-header-documentation-link"
            variant="body2"
            href={FEEDBACK_LINK}
            target="_blank"
            rel="noreferrer"
          >
            {t(keys.feedback)}
          </MuiLink>
        </div>

        {accountAddress ? (
          <Account address={accountAddress} />
        ) : (
          <Button
            data-tid="layout-header-connect-button"
            type="button"
            color="info"
            onClick={() => setShowModal(true)}
          >
            {t(keys.connect)}
          </Button>
        )}
      </div>

      <WalletModal onClose={() => setShowModal(false)} open={showModal} />
    </Box>
  );
}

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: theme.spacing(4),
  },

  info: {
    display: 'flex',
    alignItems: 'center',
  },

  links: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(7),

    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },

  logo: {
    display: 'flex',
    marginRight: theme.spacing(3),

    '&:not(:first-of-type)': {
      marginLeft: theme.spacing(2),
    },

    [theme.breakpoints.down('sm')]: {
      marginRight: theme.spacing(2),

      '> img': {
        width: 80,
      },
    },
  },

  menuButton: {
    marginRight: theme.spacing(5),
    border: 'none',
    background: 'none',
    padding: 0,
    cursor: 'pointer',

    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },

  panel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(8),
  },
}));
